import { HEBREW_HEADERS } from '@/services/consumerService'
import { formatCodeName } from '@/utils/codeName'
import { formatResourceValue, formatValue } from '@/utils/consumerDetailsFormat'

/** The consumer fields a row of the cards can edit, as the page keeps them in its edit state. */
export type EditField =
  | 'locationId'
  | 'billingId'
  | 'coordinates'
  | 'itmCoordinates'
  | 'meterNotes'

export type DetailRow = {
  key: string
  label: string
  value: string
  editField?: EditField
}

export type DetailCards = {
  consumer: DetailRow[]
  meter: DetailRow[]
  transmitter: DetailRow[]
}

type Details = Record<string, any>
type Resource = {
  name: string
  value: any
  key?: string
  type?: string | null
  valueName?: string | null
}

/**
 * The top-level fields that the consumer card or the transmitter card shows, or that no card
 * shows. The meter card lists every other field of the Gateway object, so a field the Gateway
 * adds later still appears there.
 */
const NOT_ON_METER_CARD = new Set([
  'resources',
  'splitMeterConsumptionData',
  'apiConsumerConsumptions',
  'apiDailyConsumptions',
  'premiseId',
  'meterModelId',
  'parentId',
  'supplierOfId',
  'siteId',
  'meterId',
  'meterDiscriminator',
  'meterOpsNotes',
  'amrSystemKey',
  'locationId',
  'locationName',
  'billingId',
  'coordinates',
  'itmCoordinates',
  'amrId',
  'amrModelId',
  'amrModelKey',
  'amrIsActive',
])

const hebrew = (key: string, langKey: string) =>
  langKey === 'he' ? HEBREW_HEADERS[key] : undefined

const fieldLabel = (
  key: string,
  resourceHeaders: Record<string, string>,
  langKey: string
) => resourceHeaders[key] || hebrew(key, langKey) || key

const resourceLabel = (
  resource: Resource,
  resourceHeaders: Record<string, string>,
  langKey: string
) => {
  const key = resource.key || resource.name
  const given = resourceHeaders[key]
  return given && given !== key ? given : hebrew(key, langKey) || resource.name
}

const hasText = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== ''

/** The point as the user reads and types it: latitude, then longitude. */
export const coordinatesText = (coordinates: any): string => {
  if (coordinates?.latitude != null && coordinates?.longitude != null) {
    // Six decimals are about ten centimetres, more than a meter position can claim.
    return `${+Number(coordinates.latitude).toFixed(6)},${+Number(coordinates.longitude).toFixed(6)}`
  }
  return formatValue(coordinates, 'he', 'coordinates')
}

/** What a row of the cards shows for one editable field before the user touches it. */
export const editStartValue = (details: Details, field: EditField): string => {
  switch (field) {
    case 'locationId':
      return details.locationId == null ? '' : String(details.locationId)
    case 'billingId':
      return details.billingId == null ? '' : String(details.billingId)
    case 'coordinates': {
      const text = coordinatesText(details.coordinates)
      return text === '-' ? '' : text
    }
    case 'itmCoordinates': {
      const text = formatValue(details.itmCoordinates, 'he', 'itmCoordinates')
      return text === '-' ? '' : text
    }
    case 'meterNotes':
      return details.meterNotes == null ? '' : String(details.meterNotes)
  }
}

const consumerRows = (
  details: Details,
  resourceHeaders: Record<string, string>,
  langKey: string
): DetailRow[] => {
  const orDash = (value: string) => (value === '' ? '-' : value)
  const moved: DetailRow[] = [
    {
      key: 'locationName',
      label: hebrew('locationId', langKey) || 'locationId',
      value: orDash(String(details.locationName ?? details.locationId ?? '')),
      editField: 'locationId',
    },
    {
      key: 'billingId',
      label: fieldLabel('billingId', resourceHeaders, langKey),
      value: formatValue(details.billingId, langKey, 'billingId'),
      editField: 'billingId',
    },
    {
      key: 'coordinates',
      label: fieldLabel('coordinates', resourceHeaders, langKey),
      value: orDash(editStartValue(details, 'coordinates')),
      editField: 'coordinates',
    },
    {
      key: 'itmCoordinates',
      label: fieldLabel('itmCoordinates', resourceHeaders, langKey),
      value: orDash(editStartValue(details, 'itmCoordinates')),
      editField: 'itmCoordinates',
    },
  ]
  const resources: Resource[] = details.resources ?? []
  const premise = resources
    .filter(r => r.type !== 'meters')
    .map(r => ({
      key: r.key || r.name,
      label: resourceLabel(r, resourceHeaders, langKey),
      value: r.valueName
        ? formatCodeName(r.value, r.valueName)
        : formatResourceValue(r.value, langKey),
    }))
  return [...moved, ...premise]
}

const meterRows = (
  details: Details,
  resourceHeaders: Record<string, string>,
  langKey: string
): DetailRow[] => {
  const rows: DetailRow[] = []
  for (const [key, value] of Object.entries(details)) {
    if (NOT_ON_METER_CARD.has(key)) continue
    let shown = formatValue(value, langKey, key)
    if (key === 'meterNumber' && hasText(details.meterDiscriminator)) {
      shown = `${shown} [${String(details.meterDiscriminator).trim()}]`
    }
    rows.push({
      key,
      label: fieldLabel(key, resourceHeaders, langKey),
      value: shown,
      ...(key === 'meterNotes' && { editField: 'meterNotes' as const }),
    })
  }
  const resources: Resource[] = details.resources ?? []
  for (const r of resources.filter(r => r.type === 'meters')) {
    const key = r.key || r.name
    if (rows.some(row => row.key === key)) continue
    rows.push({
      key,
      label: resourceLabel(r, resourceHeaders, langKey),
      value: r.valueName
        ? formatCodeName(r.value, r.valueName)
        : formatValue(r.value, langKey, key),
    })
  }
  return rows
}

const transmitterRows = (
  details: Details,
  resourceHeaders: Record<string, string>,
  langKey: string
): DetailRow[] => [
  {
    key: 'amrId',
    label: fieldLabel('amrId', resourceHeaders, langKey),
    value: formatValue(details.amrId, langKey, 'amrId'),
  },
  {
    key: 'amrModelKey',
    label: fieldLabel('amrModelId', resourceHeaders, langKey),
    value: formatValue(details.amrModelKey, langKey, 'amrModelKey'),
  },
]

/** The rows of the consumer, meter and transmitter cards, which the page, the copy and the exports all list. */
export const buildDetailCards = (
  details: Details | null | undefined,
  resourceHeaders: Record<string, string> = {},
  langKey = 'he'
): DetailCards =>
  details
    ? {
        consumer: consumerRows(details, resourceHeaders, langKey),
        meter: meterRows(details, resourceHeaders, langKey),
        transmitter: transmitterRows(details, resourceHeaders, langKey),
      }
    : { consumer: [], meter: [], transmitter: [] }

/** The cards in the order the page, the copy and the exports list them. */
export const CARD_ORDER = ['consumer', 'meter', 'transmitter'] as const

export const cardTitles = (langKey: string) =>
  langKey === 'he'
    ? { consumer: 'פרטי צרכן', meter: 'פרטי מד', transmitter: 'פרטי משדר' }
    : {
        consumer: 'Consumer Details',
        meter: 'Meter Details',
        transmitter: 'Transmitter Details',
      }

/** The text the copy button puts on the clipboard: each card as a title and tab-separated rows. */
export const buildConsumerDetailsClipboardText = (
  details: Details | null | undefined,
  resourceHeaders: Record<string, string> = {},
  langKey = 'he'
): string => {
  if (!details) return ''
  const cards = buildDetailCards(details, resourceHeaders, langKey)
  const titles = cardTitles(langKey)
  return CARD_ORDER.filter(card => cards[card].length > 0)
    .map(
      card =>
        `${titles[card]}\n${cards[card].map(r => `${r.label}\t${r.value}`).join('\n')}`
    )
    .join('\n\n')
}
